// Логика композера (SPEC §4.2 «Композер»). Локальный буфер текста + режим reply/edit, выбранные
// вложения (загрузка через media-pipeline с прогрессом/отменой), эмодзи-вставка, упоминания @,
// синхронизация черновика (actions.setDraft / select.draft), typing-исходящий, оптимистичная отправка
// (actions.send) и коммит правки (actions.edit). Голос — отдельным хуком useVoiceComposer (ниже файлом).
//
// Всё доменное — через стор; загрузка файла — через transport.rest (единственный сетевой путь).
//
// UPQ-2 (из форка «Наш.Чат» 03.11.2026): вложения грузятся очередью — одновременно не больше
// MAX_CONCURRENT_UPLOADS, остальные «в очереди». Слот, упавший после всех повторов (uploadFile сам
// переживает переходящие сбои, UPQ-1), можно «Повторить» — докачка с места обрыва тем же uploadId.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  ChatId,
  MessageId,
  MessageRef,
  Attachment,
  UserId,
  MessageKind,
} from '@tele-chat/shared-contracts'
import type { SendDraft } from '@tele-chat/client-store'
import { useStore } from '../store/StoreContext.js'
import { useRest } from '../transport/TransportContext.js'
import { uploadFile, type UploadResumeState } from '../media/upload.js'
import { ConcurrencyQueue } from './upload-queue.js'

export type ComposerMode = 'send' | 'record'

/** Сколько вложений одного композера грузится одновременно (остальные ждут в очереди). */
export const MAX_CONCURRENT_UPLOADS = 2

/**
 * Стадия слота вложения: queued — ждёт места в очереди; uploading — идут байты; waiting — сбой сети /
 * свёрнутое приложение, ждём и повторим сами; ready — вложение собрано; error — не вышло, есть «Повторить».
 */
export type PendingStatus = 'queued' | 'uploading' | 'waiting' | 'ready' | 'error'

export interface PendingAttachment {
  /** Готовое вложение (после complete) — его id уйдёт в send. */
  attachment?: Attachment
  /** Превью-имя/тип до завершения. */
  filename: string
  kind: Attachment['kind']
  /** 0..1 пока грузится. */
  progress: number
  /** Идентификатор слота (для удаления/отмены). */
  slotId: string
  /** Контроллер отмены незавершённой загрузки. */
  abort: AbortController
  error?: string
  /** Стадия (UPQ-2); ready ⇔ attachment задан, error ⇔ error задан. */
  status: PendingStatus
}

/** Загрузка за слотом: файл, чат, в который он добавлен, и место для докачки. */
interface UploadJob {
  slotId: string
  chatId: ChatId
  file: File
  abort: AbortController
  /** Последний подтверждённый шаг (init/кусок) — с него «Повторить» продолжит. */
  resume?: UploadResumeState
}

export interface ComposerApi {
  text: string
  setText(v: string): void
  mode: ComposerMode
  replyingTo: MessageRef | null
  editing: MessageRef | null
  pending: PendingAttachment[]
  /** Готовые Attachment для превью InputBar. */
  readyAttachments: Attachment[]
  /** Начать ответ на сообщение. */
  startReply(ref: MessageRef): void
  cancelReply(): void
  /** Начать правку своего сообщения (префилл текста). */
  startEdit(ref: MessageRef, currentText: string): void
  cancelEdit(): void
  /** Вставить эмодзи в позицию курсора (через textarea ref). */
  insertEmoji(emoji: string): void
  /** Добавить файлы (скрепка/DnD/вставка/камера) — ставит загрузки в очередь. */
  addFiles(files: File[]): void
  removeAttachment(slotId: string): void
  /** Повторить упавшую загрузку (слот в error): докачка с места обрыва, если сессия жива, иначе заново. */
  retryAttachment(slotId: string): void
  /** Отправить (текст+вложения+reply+mentions) или закоммитить правку. */
  submit(): void
  /** Текущий список упомянутых (UserId) — копится при выборе из автокомплита. */
  mentions: UserId[]
  addMention(userId: UserId, handle: string): void
  /** Привязать textarea для вставки эмодзи/упоминаний в позицию курсора. */
  bindTextarea(el: HTMLTextAreaElement | null): void
  /** uploading — нельзя отправлять, пока есть незавершённые загрузки. */
  uploading: boolean
}

let slotSeq = 0
function nextSlotId(): string {
  slotSeq += 1
  return `slot-${slotSeq}`
}

function kindFromMime(mime: string): Attachment['kind'] {
  if (mime.startsWith('image/')) return 'image'
  if (mime.startsWith('video/')) return 'video'
  if (mime.startsWith('audio/')) return 'audio'
  return 'file'
}

/**
 * Композер одного чата. На смене chatId сбрасывает локальное состояние и префиллит черновик из стора.
 * Дебаунс-персист черновика делает стор (actions.setDraft), здесь — только проброс ввода.
 */
export function useComposer(chatId: ChatId): ComposerApi {
  const store = useStore()
  const rest = useRest()

  const [text, setTextState] = useState('')
  const [replyingTo, setReplyingTo] = useState<MessageRef | null>(null)
  const [editing, setEditing] = useState<MessageRef | null>(null)
  const [pending, setPending] = useState<PendingAttachment[]>([])
  const [mentions, setMentions] = useState<UserId[]>([])
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const typingThrottle = useRef<number>(0)

  // Зеркало pending для эффекта смены чата: оборвать загрузки, начатые в прежнем чате.
  const pendingRef = useRef<PendingAttachment[]>([])
  pendingRef.current = pending

  // Очередь загрузок и их состояние вне React (файл, место докачки) — живут, пока жив композер.
  const queueRef = useRef<ConcurrencyQueue | null>(null)
  if (queueRef.current === null) queueRef.current = new ConcurrencyQueue(MAX_CONCURRENT_UPLOADS)
  const jobsRef = useRef(new Map<string, UploadJob>())
  const restRef = useRef(rest)
  restRef.current = rest

  /** Оборвать всё незавершённое и забыть очередь (смена чата, уход с экрана). */
  const dropUnfinished = useCallback((): void => {
    for (const p of pendingRef.current) if (p.attachment === undefined) p.abort.abort()
    for (const job of jobsRef.current.values()) job.abort.abort()
    jobsRef.current.clear()
    queueRef.current?.clear()
  }, [])

  // Префилл черновика при входе в чат (FR-MSG-12 / FR-PWA-05). Экран не пересоздаётся при смене
  // :chatId (тот же элемент маршрута) — незавершённые загрузки прежнего чата обрываем, а не бросаем
  // догружаться вхолостую (их слоты всё равно сброшены, отправить их некуда). Очередь — тоже.
  useEffect(() => {
    dropUnfinished()
    setTextState(store.select.draft(chatId))
    setReplyingTo(null)
    setEditing(null)
    setPending([])
    setMentions([])
  }, [chatId, store, dropUnfinished])

  // Ушли с экрана переписки — слоты пропадают вместе с композером, догружать их некому отправить:
  // обрываем (иначе повторы UPQ-1 крутились бы вхолостую до исчерпания попыток).
  useEffect(() => dropUnfinished, [dropUnfinished])

  const patchSlot = useCallback((slotId: string, patch: Partial<PendingAttachment>): void => {
    setPending((list) => list.map((p) => (p.slotId === slotId ? { ...p, ...patch } : p)))
  }, [])

  /** Задача очереди: загрузка одного слота (с места докачки, если оно есть). Исход — в слот. */
  const runJob = useCallback(
    (job: UploadJob): Promise<void> => {
      const { signal } = job.abort
      if (signal.aborted) return Promise.resolve()
      patchSlot(job.slotId, { status: 'uploading' })
      return uploadFile(
        restRef.current,
        job.chatId,
        job.file,
        { filename: job.file.name, mime: job.file.type || 'application/octet-stream' },
        {
          signal,
          resume: job.resume,
          onCheckpoint: (state) => {
            job.resume = state
          },
          onPhase: (phase) => {
            if (!signal.aborted) patchSlot(job.slotId, { status: phase })
          },
          onProgress: (fraction) => {
            if (!signal.aborted) patchSlot(job.slotId, { progress: fraction })
          },
        },
      ).then(
        (attachment) => {
          if (!signal.aborted) patchSlot(job.slotId, { attachment, progress: 1, status: 'ready' })
        },
        (err: unknown) => {
          if (signal.aborted) return
          const message = err instanceof Error ? err.message : 'upload-failed'
          patchSlot(job.slotId, { error: message, status: 'error' })
        },
      )
    },
    [patchSlot],
  )

  const setText = useCallback(
    (v: string): void => {
      setTextState(v)
      // Черновик — в стор (стор дебаунсит персист на сервер).
      if (editing === null) store.actions.setDraft(chatId, v)
      // Typing-исходящий с лёгким троттлом на стороне app-ui (стор тоже троттлит).
      const nowTs = Date.now()
      if (v.length > 0 && nowTs - typingThrottle.current > 2500) {
        typingThrottle.current = nowTs
        store.actions.notifyTyping(chatId, 'typing')
      }
    },
    [chatId, store, editing],
  )

  const bindTextarea = useCallback((el: HTMLTextAreaElement | null): void => {
    textareaRef.current = el
  }, [])

  const insertAtCursor = useCallback(
    (insert: string): void => {
      const el = textareaRef.current
      if (el === null) {
        setText(text + insert)
        return
      }
      const start = el.selectionStart ?? text.length
      const end = el.selectionEnd ?? text.length
      const next = text.slice(0, start) + insert + text.slice(end)
      setText(next)
      // Возвращаем курсор после вставки (после ре-рендера).
      queueMicrotask(() => {
        el.focus()
        const pos = start + insert.length
        el.setSelectionRange(pos, pos)
      })
    },
    [text, setText],
  )

  const insertEmoji = useCallback((emoji: string): void => insertAtCursor(emoji), [insertAtCursor])

  const addMention = useCallback(
    (userId: UserId, handle: string): void => {
      // Заменяем хвостовой «@…» (если печатали) или просто вставляем «@handle ».
      const el = textareaRef.current
      const token = `@${handle} `
      if (el !== null) {
        const start = el.selectionStart ?? text.length
        const before = text.slice(0, start)
        const atIdx = before.lastIndexOf('@')
        if (atIdx >= 0 && /^@[\w.-]*$/.test(before.slice(atIdx))) {
          const next = text.slice(0, atIdx) + token + text.slice(start)
          setText(next)
        } else {
          insertAtCursor(token)
        }
      } else {
        insertAtCursor(token)
      }
      setMentions((list) => (list.includes(userId) ? list : [...list, userId]))
    },
    [text, setText, insertAtCursor],
  )

  const startReply = useCallback((ref: MessageRef): void => {
    setEditing(null)
    setReplyingTo(ref)
  }, [])
  const cancelReply = useCallback((): void => setReplyingTo(null), [])

  const startEdit = useCallback(
    (ref: MessageRef, currentText: string): void => {
      setReplyingTo(null)
      setEditing(ref)
      setTextState(currentText)
    },
    [],
  )
  const cancelEdit = useCallback((): void => {
    setEditing(null)
    setTextState(store.select.draft(chatId))
  }, [store, chatId])

  const addFiles = useCallback(
    (files: File[]): void => {
      if (files.length === 0) return
      const slots: PendingAttachment[] = []
      const jobs: UploadJob[] = []
      for (const file of files) {
        const slotId = nextSlotId()
        const abort = new AbortController()
        slots.push({
          filename: file.name,
          kind: kindFromMime(file.type),
          progress: 0,
          slotId,
          abort,
          status: 'queued',
        })
        // chatId слота фиксируется в момент добавления: загрузка идёт туда, куда файл положили.
        jobs.push({ slotId, chatId, file, abort })
      }
      setPending((list) => [...list, ...slots])
      for (const job of jobs) {
        jobsRef.current.set(job.slotId, job)
        queueRef.current?.enqueue(job.slotId, () => runJob(job))
      }
    },
    // chatId обязателен: экран переиспользуется между чатами, и без него загрузка уходила в ПЕРВЫЙ
    // открытый чат (прод форка «Наш.Чат» 09.09.2026: вложения легли в чужой чат → 403 у получателя).
    // rest — по-прежнему в deps (новый транспорт = новый колбэк), сам вызов берёт его из restRef.
    [rest, chatId, runJob],
  )

  const removeAttachment = useCallback((slotId: string): void => {
    queueRef.current?.remove(slotId)
    const job = jobsRef.current.get(slotId)
    job?.abort.abort()
    jobsRef.current.delete(slotId)
    // место убранной (оборванной) загрузки сразу отдаём следующей в очереди
    queueRef.current?.release(slotId)
    setPending((list) => {
      const slot = list.find((p) => p.slotId === slotId)
      slot?.abort.abort()
      return list.filter((p) => p.slotId !== slotId)
    })
  }, [])

  const retryAttachment = useCallback(
    (slotId: string): void => {
      const job = jobsRef.current.get(slotId)
      const slot = pendingRef.current.find((p) => p.slotId === slotId)
      if (job === undefined || slot === undefined || slot.status !== 'error' || job.abort.signal.aborted) return
      // Слот снова в очереди; место докачки (job.resume) сохранено — uploadFile продолжит с него,
      // а если сессию уже собрал GC (NOT_FOUND) — сам начнёт заново.
      patchSlot(slotId, { status: 'queued', error: undefined })
      queueRef.current?.enqueue(slotId, () => runJob(job))
    },
    [patchSlot, runJob],
  )

  const readyAttachments = useMemo<Attachment[]>(
    () => pending.map((p) => p.attachment).filter((a): a is Attachment => a !== undefined),
    [pending],
  )

  const uploading = pending.some((p) => p.attachment === undefined && p.error === undefined)

  const submit = useCallback((): void => {
    const trimmed = text.trim()

    // Режим правки — коммитим edit (оптимистично; стор откатывает при ошибке).
    if (editing !== null) {
      if (trimmed.length === 0) return
      void store.actions.edit(editing.messageId, trimmed)
      setEditing(null)
      setTextState(store.select.draft(chatId))
      return
    }

    if (uploading) return
    const attachmentIds = readyAttachments.map((a) => a.id)
    const hasMedia = attachmentIds.length > 0
    if (trimmed.length === 0 && !hasMedia) return

    const kind: MessageKind = hasMedia ? 'media' : 'text'
    const draft: SendDraft = {
      kind,
      text: trimmed.length > 0 ? trimmed : undefined,
      attachmentIds: hasMedia ? attachmentIds : undefined,
      replyTo: replyingTo?.messageId,
      mentions: mentions.length > 0 ? mentions : undefined,
    }
    // Оптимистичный пузырь появляется в ленте сразу (стор §4.5); clientMsgId генерит стор/транспорт.
    store.actions.send(chatId, draft)

    // Чистим композер и черновик. Незавершённых загрузок здесь нет (uploading блокирует отправку),
    // упавшие слоты уходят вместе с остальными — их задачи забываем.
    setTextState('')
    store.actions.setDraft(chatId, '')
    setReplyingTo(null)
    setPending([])
    jobsRef.current.clear()
    queueRef.current?.clear()
    setMentions([])
  }, [text, editing, uploading, readyAttachments, replyingTo, mentions, store, chatId])

  const mode: ComposerMode = text.trim().length > 0 || readyAttachments.length > 0 ? 'send' : 'record'

  return {
    text,
    setText,
    mode,
    replyingTo,
    editing,
    pending,
    readyAttachments,
    startReply,
    cancelReply,
    startEdit,
    cancelEdit,
    insertEmoji,
    addFiles,
    removeAttachment,
    retryAttachment,
    submit,
    mentions,
    addMention,
    bindTextarea,
    uploading,
  }
}

/** Строит MessageRef для reply/edit из Message (короткая цитата). */
export function messageRefFor(message: {
  id: MessageId
  seq: number
  senderId: UserId
  kind: MessageKind
  text?: string
}): MessageRef {
  return {
    messageId: message.id,
    seq: message.seq as MessageRef['seq'],
    senderId: message.senderId,
    preview: message.text ?? '',
    kind: message.kind,
  }
}
